// Menu Financeiro (2026-09-28) — backend src/finance/. Faturas vêm ao vivo
// do Asaas (cache de 5 min no servidor); despesas/entradas são lançamentos
// próprios da empresa.
import { gpsDriveDelete, gpsDriveGet, gpsDrivePatch, gpsDrivePost } from "@/lib/api/gpsdrive-client";

export type FinanceAction = "view" | "invoices" | "expenses" | "create" | "edit" | "delete";
export type FinanceAccess = Record<FinanceAction, boolean>;

export interface FinanceOpenInvoice {
  id: string;
  customerId: string;
  clientName: string;
  userId: string | null;
  value: number;
  dueDate: string;
  status: "pendente" | "vencida";
  daysLate: number;
  billingType: string;
  description: string | null;
  invoiceUrl: string | null;
}

export interface FinanceDebtor {
  customerId: string;
  clientName: string;
  userId: string | null;
  invoices: number;
  total: number;
  oldestDueDate: string;
  daysLate: number;
}

interface CountTotal {
  count: number;
  total: number;
}

// Faturas vêm de uma cópia local do Asaas atualizada em segundo plano a
// cada 10 min (a conta real tem dezenas de milhares de cobranças — ler ao
// vivo travava o painel). `pending` = primeira cópia ainda em andamento.
export interface FinanceSyncInfo {
  syncedAt: string | null;
  running: boolean;
  lastError: string | null;
  // Barra de progresso: só presente enquanto sincroniza.
  progress: { phase: "clientes" | "faturas" | "atualizando"; done: number; total: number } | null;
}

export type FinanceInvoicesSection =
  | { configured: false }
  | { configured: true; error: string }
  | { configured: true; error: null; pending: true; sync: FinanceSyncInfo }
  | {
      configured: true;
      error: null;
      pending: false;
      sync: FinanceSyncInfo;
      faturado: number;
      faturadoCount: number;
      recebido: number;
      recebidoLiquido: number;
      recebidoCount: number;
      aVencer: CountTotal;
      vencido: CountTotal;
      emAberto: CountTotal;
      inadimplenciaPeriodo: number;
      inadimplenciaClientes: number;
      clientesDevendo: number;
      ticketMedio: number;
      status: Record<"paga" | "pendente" | "vencida" | "estornada", CountTotal>;
      aging: { label: string; count: number; total: number }[];
      debtors: FinanceDebtor[];
      openInvoices: FinanceOpenInvoice[];
    };

export interface FinanceTransactionsSection {
  despesas: number;
  despesasPagas: number;
  despesasAPagar: number;
  despesasVencidas: number;
  entradasManuais: number;
  porCategoria: { category: string; total: number }[];
}

export interface FinanceMonth {
  month: string;
  faturado: number;
  recebido: number;
  despesas: number;
  entradasManuais: number;
  entradas: number;
  saidas: number;
  saldo: number;
}

export interface FinanceSummary {
  period: { from: string; to: string };
  today: string;
  access: FinanceAccess;
  invoices: FinanceInvoicesSection | null;
  transactions: FinanceTransactionsSection | null;
  cash: { entradas: number; saidas: number; saldo: number };
  series: FinanceMonth[];
}

export type FinanceTransactionType = "expense" | "income";

export interface FinanceTransaction {
  id: string;
  type: FinanceTransactionType;
  description: string;
  category: string;
  amount: number;
  date: string;
  paid: boolean;
  paidAt: string | null;
  paymentMethod: string | null;
  notes: string | null;
  createdAt: string;
  // Parcelada ("3/10") ou fixa mensal (count null = sem fim).
  series: {
    id: string;
    kind: "installment" | "fixed";
    index: number;
    count: number | null;
    endDate: string | null;
    active: boolean;
  } | null;
}

export type FinanceTransactionInput = Omit<FinanceTransaction, "id" | "createdAt" | "series">;

export type FinanceRecurrence =
  | { kind: "installment"; count: number; amountIs: "total" | "installment" }
  | { kind: "fixed"; endDate: string | null };

// Lançamento de uma série: só este, este e os próximos, ou todos.
export type FinanceSeriesScope = "one" | "future" | "all";

export const gpsDriveFinanceService = {
  access: () => gpsDriveGet<FinanceAccess>("/api/v1/finance/access"),
  summary: (from: string, to: string, refresh = false) =>
    gpsDriveGet<FinanceSummary>(`/api/v1/finance/summary?from=${from}&to=${to}${refresh ? "&refresh=1" : ""}`),
  listTransactions: (params: { from?: string; to?: string; type?: FinanceTransactionType }) => {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => !!v) as [string, string][]);
    return gpsDriveGet<FinanceTransaction[]>(`/api/v1/finance/transactions?${q.toString()}`);
  },
  createTransaction: (input: FinanceTransactionInput, recurrence: FinanceRecurrence | null = null) =>
    gpsDrivePost<FinanceTransaction>("/api/v1/finance/transactions", { ...input, recurrence }),
  updateTransaction: (id: string, input: Partial<FinanceTransactionInput>, scope: FinanceSeriesScope = "one") =>
    gpsDrivePatch<FinanceTransaction>(`/api/v1/finance/transactions/${id}?scope=${scope}`, input),
  deleteTransaction: (id: string, scope: FinanceSeriesScope = "one") =>
    gpsDriveDelete<void>(`/api/v1/finance/transactions/${id}?scope=${scope}`),
};
